import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import './DigCompCopSections.css'

/**
 * DigComp Community of Practice sections for /for/digcomp (#4724).
 *
 * Rendered by HomePageV2 only when the audience slug is `digcomp`; they
 * replace the generic "Why Tacit?" and "For Teams & Educators" blocks there.
 *
 * HUMAN_OVERSIGHT_ANCHOR is linked from a CoP forum post
 * (`/for/digcomp#human-oversight`), so the id must not change. The section
 * renders synchronously (its heading does not wait for scenario data) and
 * sits directly under the hero, so nothing above it changes height once the
 * scenario and outcome data arrive.
 */

export const HUMAN_OVERSIGHT_ANCHOR = 'human-oversight'

// Seeded by the #4724 migration lane. Matched by English name; a name that
// is not in the audience preview data (migration not yet run, scenario
// archived) hides its card instead of rendering a broken one.
export const FEATURED_OVERSIGHT_SCENARIOS = [
  'Public Services: Check an AI Benefits Decision',
  'HR: Spot AI Bias in Recruitment',
  'Customer Service: Check an AI-Drafted Reply',
]

export const CoP_WORKSHOP_URL = 'https://public.3.basecamp.com/p/V1YCeLUgT9HeGacw9cGee9Ev/vault'

const VISIBLE_OUTCOMES = 2

// The preview endpoint translates `name` under ?lang=; `source_name` keeps
// the English name the outcome map and the featured list are keyed by.
export function englishName(scenario) {
  return scenario?.source_name || scenario?.name || ''
}

export function findFeaturedScenarios(scenarios, names = FEATURED_OVERSIGHT_SCENARIOS) {
  const byName = new Map()
  for (const s of scenarios || []) {
    const key = englishName(s)
    if (key && !byName.has(key)) byName.set(key, s)
  }
  return names.map((name) => byName.get(name)).filter(Boolean)
}

/**
 * AI-Explicit learning outcomes for one scenario from digcompOutcomeMap.json
 * (`scenarios[name].outcomes`, each `{ id, text, ai }`). Tolerates a missing
 * map, a missing scenario entry and duplicate ids.
 */
export function aiExplicitOutcomes(outcomeMap, name) {
  const outcomes = outcomeMap?.scenarios?.[name]?.outcomes
  if (!Array.isArray(outcomes)) return []
  const seen = new Set()
  const result = []
  for (const o of outcomes) {
    if (!o || o.ai !== 'AI-Explicit' || !o.id) continue
    const text = o.text ?? o.statement ?? ''
    if (!text || seen.has(o.id)) continue
    seen.add(o.id)
    result.push({ id: o.id, text })
  }
  return result
}

function OutcomeList({ outcomes, listId }) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  if (outcomes.length === 0) return null
  const hiddenCount = outcomes.length - VISIBLE_OUTCOMES
  const shown = expanded ? outcomes : outcomes.slice(0, VISIBLE_OUTCOMES)
  return (
    <div className="dc-cop-outcomes">
      <h4 className="dc-cop-outcomes__heading">{t('digcomp.cop.outcomesHeading')}</h4>
      <ul className="dc-cop-outcomes__list" id={listId}>
        {shown.map((o) => (
          <li key={o.id} className="dc-cop-outcomes__item">
            <span className="dc-cop-outcomes__id">{o.id}</span>
            <span className="dc-cop-outcomes__text">{o.text}</span>
          </li>
        ))}
      </ul>
      {hiddenCount > 0 && (
        <button
          type="button"
          className="dc-cop-outcomes__toggle"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? t('digcomp.cop.showFewerOutcomes')
            : t('digcomp.cop.showMoreOutcomes', { count: hiddenCount })}
        </button>
      )}
    </div>
  )
}

OutcomeList.propTypes = {
  outcomes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, text: PropTypes.string })).isRequired,
  listId: PropTypes.string.isRequired,
}

export function HumanOversightSection({ scenarios, isLoading, loadError, startError, outcomeMap, onStartScenario, loadingScenarioId }) {
  const { t } = useTranslation()
  const featured = useMemo(() => findFeaturedScenarios(scenarios), [scenarios])

  return (
    <section
      id={HUMAN_OVERSIGHT_ANCHOR}
      className="dc-cop-section dc-cop-oversight"
      aria-labelledby="dc-cop-oversight-heading"
    >
      <div className="dc-cop-container">
        <p className="dc-cop-eyebrow">{t('digcomp.cop.oversightEyebrow')}</p>
        <h2 id="dc-cop-oversight-heading" className="dc-cop-heading">{t('digcomp.cop.oversightHeading')}</h2>
        <p className="dc-cop-lede">{t('digcomp.cop.oversightBody')}</p>
        <p className="dc-cop-lede">{t('digcomp.cop.oversightArticle4')}</p>

        {startError && <p className="dc-cop-error" role="alert">{startError}</p>}

        {isLoading ? (
          <p className="dc-cop-status">{t('scenarios.loading')}</p>
        ) : featured.length === 0 ? (
          <p className="dc-cop-status">
            <a href="#scenarios" className="dc-cop-link">
              {loadError ? t('digcomp.cop.oversightLoadError') : t('digcomp.cop.oversightFallback')}
            </a>
          </p>
        ) : (
          <ul className="dc-cop-cards" aria-label={t('digcomp.cop.oversightCardsLabel')}>
            {featured.map((scenario) => {
              const outcomes = aiExplicitOutcomes(outcomeMap, englishName(scenario))
              const isThisLoading = loadingScenarioId === scenario.id
              return (
                <li key={scenario.id} className="dc-cop-card" data-featured-scenario={englishName(scenario)}>
                  <div className="dc-cop-card__head">
                    {scenario.avatar_url ? (
                      <img src={scenario.avatar_url} alt="" className="dc-cop-card__avatar" width="48" height="48" />
                    ) : (
                      <span className="dc-cop-card__avatar dc-cop-card__avatar--ph" aria-hidden="true">
                        {(scenario.ai_role || scenario.name || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                    <h3 className="dc-cop-card__title">{scenario.name}</h3>
                  </div>
                  {scenario.summary && <p className="dc-cop-card__summary">{scenario.summary}</p>}
                  <OutcomeList outcomes={outcomes} listId={`dc-cop-outcomes-${scenario.id}`} />
                  <button
                    type="button"
                    className="v2-btn v2-btn-primary dc-cop-card__start"
                    onClick={() => onStartScenario && onStartScenario(scenario)}
                    disabled={!!loadingScenarioId}
                    aria-busy={isThisLoading || undefined}
                  >
                    {t('digcomp.cop.startScenario')}
                    <span className="dc-cop-sr-only">{`: ${scenario.name}`}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}

HumanOversightSection.propTypes = {
  scenarios: PropTypes.array,
  isLoading: PropTypes.bool,
  loadError: PropTypes.bool,
  startError: PropTypes.string,
  outcomeMap: PropTypes.object,
  onStartScenario: PropTypes.func,
  loadingScenarioId: PropTypes.number,
}

export function HowWeMapSection() {
  const { t } = useTranslation()
  return (
    <section id="how-we-map" className="dc-cop-section dc-cop-map" aria-labelledby="dc-cop-map-heading">
      <div className="dc-cop-container">
        <p className="dc-cop-eyebrow">{t('digcomp.cop.mapEyebrow')}</p>
        <h2 id="dc-cop-map-heading" className="dc-cop-heading">{t('digcomp.cop.mapHeading')}</h2>
        <ol className="dc-cop-steps">
          <li className="dc-cop-step">
            <h3 className="dc-cop-step__title">{t('digcomp.cop.mapStep1Title')}</h3>
            <p className="dc-cop-step__body">{t('digcomp.cop.mapStep1Body')}</p>
          </li>
          <li className="dc-cop-step">
            <h3 className="dc-cop-step__title">{t('digcomp.cop.mapStep2Title')}</h3>
            <p className="dc-cop-step__body">{t('digcomp.cop.mapStep2Body')}</p>
          </li>
          <li className="dc-cop-step">
            <h3 className="dc-cop-step__title">{t('digcomp.cop.mapStep3Title')}</h3>
            <p className="dc-cop-step__body">{t('digcomp.cop.mapStep3Body')}</p>
          </li>
        </ol>
        <p className="dc-cop-credit">{t('digcomp.cop.mapCredit')}</p>
      </div>
    </section>
  )
}

export function FromCopSection() {
  const { t } = useTranslation()
  return (
    <section id="digcomp-cop" className="dc-cop-section dc-cop-workshop" aria-labelledby="dc-cop-workshop-heading">
      <div className="dc-cop-container">
        <p className="dc-cop-eyebrow">{t('digcomp.cop.copEyebrow')}</p>
        <h2 id="dc-cop-workshop-heading" className="dc-cop-heading">{t('digcomp.cop.copHeading')}</h2>
        <p className="dc-cop-lede">{t('digcomp.cop.copBody')}</p>
        <a className="dc-cop-link dc-cop-link--strong" href={CoP_WORKSHOP_URL} target="_blank" rel="noopener">
          {t('digcomp.cop.copLink')}
          <span className="dc-cop-sr-only">{` (${t('digcomp.cop.opensInNewTab')})`}</span>
          <span aria-hidden="true"> &#8599;</span>
        </a>
      </div>
    </section>
  )
}

/** #4724: top-left link home on /for/digcomp. A plain anchor, so it is a crawlable link to the homepage. */
export function DigCompHomeHeader() {
  const { t } = useTranslation()
  return (
    <header className="dc-cop-home-header">
      <a href="/" className="dc-cop-home-link" aria-label={t('digcomp.cop.homeLinkLabel')}>
        {t('digcomp.cop.homeLink')}
      </a>
    </header>
  )
}

export function DigCompContactSection({ onSignIn }) {
  const { t } = useTranslation()
  return (
    <section className="dc-cop-section dc-cop-contact" aria-labelledby="dc-cop-contact-heading">
      <div className="dc-cop-container">
        <h2 id="dc-cop-contact-heading" className="dc-cop-heading dc-cop-heading--small">{t('digcomp.cop.contactHeading')}</h2>
        <p className="dc-cop-lede">{t('digcomp.cop.contactBody')}</p>
        <div className="dc-cop-actions">
          <a href="mailto:hello@tacit.coach?subject=DigComp" className="v2-btn v2-btn-primary dc-cop-actions__btn">
            {t('b2b.contactUs')}
          </a>
          <button type="button" className="v2-btn v2-btn-secondary dc-cop-actions__btn" onClick={onSignIn}>
            {t('hero.signIn')}
          </button>
        </div>
      </div>
    </section>
  )
}

DigCompContactSection.propTypes = {
  onSignIn: PropTypes.func.isRequired,
}
